import React, { useMemo, useState } from "react"
import { StyleSheet, View } from "react-native"
import TextM from "@common/components/text/TextM"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import CorrectionPointCard from "./CorrectionPointCard"
import { TYPE_ORDER, typeConfig } from "./correctionPointTypes"

/**
 * Shared, surface-agnostic display of a structured AI correction.
 *
 * One list of point cards in MARKER ORDER (①②③ — the narrative of the copy),
 * the type carried by color. The per-type chips are non-exclusive FILTERS:
 * toggling one off dims its cards (they never disappear, so the ①②③ sequence
 * stays readable). With a single type present, the chips row is omitted.
 *
 * It holds NO surface-specific logic (no tutor/student wording) so it can be
 * reused as-is by the student surface — the consuming surface owns the title,
 * any feedback action, and the copy image beside/above it.
 *
 * Selection is controlled by the parent (`selectedIndex` / `onSelectPoint`):
 * tapping a card selects its point, which the tutor surface mirrors on the
 * copy's markers (and vice-versa). `onCardLayout(index, y)` reports each
 * card's y within the block so the parent can scroll a card into view.
 *
 * On native, `MarkdownWithLatex` is a WebView (heavy). To avoid mounting one
 * per card, the cards are an accordion on native — only the open card renders
 * its explication (≤ 1 WebView at a time), auto-sized to its content. On web
 * the renderer is cheap (react-markdown), so every card stays expanded.
 *
 * Expected point shape (from ActivityResource.correction_points):
 *   { index:int (1-based, matches the copy markers), type:'erreur'|'vigilance'|'réussite',
 *     notion:string, skill_part_name:string|null, inconnue:bool,
 *     explication:string(md/latex), certain:bool, anchor:object|null }
 *
 * @param {Array} points  the correction points (empty/absent → renders nothing)
 * @param {string} [title] optional heading rendered above the list
 * @param {React.ReactNode} [headerAccessory] optional node rendered next to the
 *        title (e.g. a surface-specific info tooltip) — the block stays generic
 * @param {number|null} [selectedIndex] the selected point's `index` (1-based)
 * @param {Function} [onSelectPoint] (index|null) => void
 * @param {Function} [onCardLayout] (index, y) => void — card offsets within the block
 * @param {object} [style] optional container style override
 */

export const StructuredCorrectionBlock = ({
    points,
    title,
    headerAccessory,
    selectedIndex = null,
    onSelectPoint,
    onCardLayout,
    style,
}) => {
    // Points in marker order — the narrative of the copy. Defensive sort:
    // the backend already emits them 1..n.
    const orderedPoints = useMemo(
        () =>
            [...(points ?? [])].sort(
                (a, b) => (a?.index ?? 0) - (b?.index ?? 0)
            ),
        [points]
    )

    // Known types first (in order), then any unexpected type — never drop data.
    const presentTypes = useMemo(() => {
        const list = orderedPoints
        const present = TYPE_ORDER.filter((t) => list.some((p) => p?.type === t))
        const extra = [...new Set(list.map((p) => p?.type))].filter(
            (t) => t && !TYPE_ORDER.includes(t)
        )
        return [...present, ...extra]
    }, [orderedPoints])

    // Non-exclusive filters: a muted type DIMS its cards, never hides them.
    const [mutedTypes, setMutedTypes] = useState([])
    const toggleType = (type) =>
        setMutedTypes((muted) =>
            muted.includes(type)
                ? muted.filter((t) => t !== type)
                : [...muted, type]
        )

    // Native accordion state: which point's explication is open. Defaults to
    // the first card; selecting a point (from a card OR a copy marker) opens it.
    const [expandedIndex, setExpandedIndex] = useState(null)
    const collapsible = !IS_WEB
    const openIndex =
        selectedIndex ?? expandedIndex ?? orderedPoints[0]?.index ?? null

    if (!orderedPoints.length) return null

    const handleCardPress = (point) => {
        const isSelected = selectedIndex === point?.index
        setExpandedIndex(point?.index)
        onSelectPoint?.(isSelected ? null : point?.index)
    }

    return (
        <View style={[styles.container, style]}>
            {title ? (
                <View style={styles.header}>
                    <TextM fontWeight="Bold" fontSize={16}>
                        {title}
                    </TextM>
                    {headerAccessory ?? null}
                </View>
            ) : null}

            {presentTypes.length > 1 && (
                <View style={styles.chips}>
                    {presentTypes.map((type) => {
                        const cfg = typeConfig(type)
                        const active = !mutedTypes.includes(type)
                        const count = orderedPoints.filter(
                            (p) => p?.type === type
                        ).length
                        return (
                            <TouchableRippleM
                                key={type}
                                onPress={() => toggleType(type)}
                                style={[
                                    styles.chip,
                                    { borderColor: cfg.color },
                                    active
                                        ? { backgroundColor: cfg.light }
                                        : styles.chipMuted,
                                ]}
                            >
                                <TextM
                                    fontWeight="Bold"
                                    fontSize={12}
                                    color={active ? cfg.color : theme.colors.grey}
                                >
                                    {cfg.label} · {count}
                                </TextM>
                            </TouchableRippleM>
                        )
                    })}
                </View>
            )}

            {orderedPoints.map((point) => (
                <CorrectionPointCard
                    key={point?.index}
                    point={point}
                    dimmed={mutedTypes.includes(point?.type)}
                    selected={selectedIndex === point?.index}
                    collapsible={collapsible}
                    expanded={openIndex === point?.index}
                    onPress={
                        onSelectPoint || collapsible
                            ? () => handleCardPress(point)
                            : null
                    }
                    onLayout={
                        onCardLayout
                            ? (e) =>
                                  onCardLayout(
                                      point?.index,
                                      e.nativeEvent.layout.y
                                  )
                            : undefined
                    }
                />
            ))}
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        marginTop: 12,
        gap: 10,
    },
    header: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    chips: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 8,
    },
    chip: {
        paddingVertical: 5,
        paddingHorizontal: 12,
        borderRadius: 20,
        borderWidth: 1.5,
    },
    chipMuted: {
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
})

export default StructuredCorrectionBlock
